"use client";
import React, { useState, useEffect } from "react";
import Image from "next/image";
import * as Icon from "@phosphor-icons/react/dist/ssr";
import { useTranslations } from "next-intl";
import { Link, useRouter } from "@/navigation";
import useMenuMobile from "@/store/useMenuMobile";
import { menus } from "@/lib/menu";
import { useModalInquiryContext } from "@/context/ModalInquiryContext";
import { RightOutlined, DownOutlined } from "@ant-design/icons";
import { useCategory } from "@/context/CategoriesContext";
import { filterCateImg } from "@/components/Home2/Collection";
import { getBlogCls } from "@/lib/api/blog";
import { Blog } from "@/lib/@types/api/blog";
import { defaultLocale } from "@/config";
import FlagIcon from "../../Flag/index";
import { SITE_LOCALES } from "@/lib/constant";
import { type TLocale } from "@/lib/@types/locale";
import { usePathname } from "next/navigation";
import Translate from "@/components/Translate/index";
import Search from "./search";
import InquiryBtn from "./InquiryBtn";
import CartDrawer from "@/components/Product/ShoppingCart/cartDrawer";
import CountBadge from "@/components/Badge/count-badge";
import { useShoppingCartStore } from "@/lib/store/shoppingCart";
import NavUser from "@/components/User/nav-user";
import clsx from "clsx";
import { useLoveStore } from "@/lib/store/love.store";

const MenuTwo = ({ locale }: { locale: string }) => {
	const router = useRouter();
	const t = useTranslations();
	const { openModalInquiry } = useModalInquiryContext();
	const { openMenuMobile, handleMenuMobile } = useMenuMobile();
	const { categories } = useCategory();
	const { loveIds } = useLoveStore();
	const [loveCount, setLoveCount] = useState(0);
	const [blogCls, setBlogCls] = useState<Blog.BlogCl[]>([]);
	const [showService, setShowService] = useState(false);
	const [showChildrenID, setShowChildrenID] = useState(-1);
	const [showGrandChildIndex, setShowGrandChildIndex] = useState(-1);
	const [isOpenLanguage, setIsOpenLanguage] = useState(false);
	const [currentSlug, setCurrentSlug] = useState("");
	const currentLocales: TLocale[] = SITE_LOCALES;
	const [count, setCount] = useState(0);
	const pathname = usePathname();
	const { cartList } = useShoppingCartStore();

	useEffect(() => {
		getBlogCls({ lang_code: { lang_code: locale } }).then((res) => {
			if (res) setBlogCls(res);
		});
	}, []);

	useEffect(() => {
		setCount(cartList?.lines?.length || 0);
	}, [cartList]);

	useEffect(() => {
		if (pathname) {
			setCurrentSlug(locale === defaultLocale ? pathname : pathname.replace(locale, ""));
		}
	}, [pathname]);

	useEffect(() => {
		setLoveCount(loveIds.length > 99 ? 99 : loveIds.length);
	}, [loveIds]);

	const setMenuShow = (id: number) => {
		if (id === 2) {
			setShowService(!showService);
		} else {
			setShowChildrenID(showChildrenID === id ? -1 : id);
		}
	};

	const setgs = (index: number) => {
		setShowGrandChildIndex(showGrandChildIndex === index ? -1 : index);
	};

	const GoToPage = (url: string) => {
		router.push(url);
		handleMenuMobile();
	};

	return (
		<>
			<header className="header-menu style-one border-brand-ink/6 fixed top-[32px] z-[100] h-[56px] w-full border-b bg-white">
				<div className="mx-auto flex h-full max-w-[1580px] items-center justify-between px-6 sm:px-10 lg:px-16">
					{/* Mobile hamburger */}
					<div className="flex items-center lg:hidden" onClick={handleMenuMobile}>
						<i className="icon-category text-2xl text-brand-ink" />
					</div>

					{/* Logo */}
					<Link href={"/"} className="flex items-center gap-2.5">
						<Image
							src="/image/logo.png"
							width={48}
							height={34}
							alt="LIGHTEW"
							className="inline-block object-contain"
						/>
						<span className="hidden font-body text-[16px] font-semibold tracking-[0.04em] text-brand-ink sm:inline">
							LIGHTEW
						</span>
					</Link>

					{/* Desktop nav */}
					<nav className="menu-main h-full max-lg:hidden">
						<ul className="flex h-full items-center gap-7">
							{menus.map(
								(item) =>
									item.show && (
										<li
											className={clsx("group h-full", (item.id === 9 || item.id === 15) && "relative")}
											key={item.id}
										>
											{!item.hasSlug && item.id !== 15 ? (
												<div className="flex h-full items-center font-body text-[13px] font-medium uppercase tracking-[0.06em] text-brand-ink/65 transition-colors duration-200 hover:text-brand-ink">
													{t(item.name)}
												</div>
											) : (
												<Link
													href={item.link}
													className="flex h-full items-center font-body text-[13px] font-medium uppercase tracking-[0.06em] text-brand-ink/65 transition-colors duration-200 hover:text-brand-ink"
												>
													{t(item.name)}
												</Link>
											)}
											{item.children && (
												<div className="sub-menu absolute -left-0 max-h-[500px] overflow-y-auto rounded-b-sm bg-white shadow-[0_8px_32px_-4px_rgba(26,14,12,0.1)]">
													<ul>
														{item.children.map((child, index) => (
															<li key={index} className="w-full cursor-pointer text-nowrap px-4 py-2.5">
																<Link
																	href={child.link}
																	className="hover:text-brand-fg block font-body text-[13px] text-brand-ink/65 transition-colors duration-200"
																>
																	{t(child.name)}
																</Link>
															</li>
														))}
													</ul>
												</div>
											)}
										</li>
									),
							)}
						</ul>
					</nav>

					{/* Right actions */}
					<div className="flex items-center">
						{/* CTA */}
						<button
							onClick={openModalInquiry}
							className="rounded-full bg-brand px-4 py-1.5 font-body text-[12px] font-medium tracking-[0.06em] text-white transition-colors duration-200 hover:bg-brand/90 max-sm:hidden"
						>
							{t("home.Get In Touch")}
						</button>

						{/* 分隔 */}
						<span className="mx-3 hidden h-4 w-px bg-brand-ink/10 sm:block" />

						{/* 图标组 — 统一 32px 圆形，18px 图标 */}
						<div className="flex items-center gap-1">
							<Search lastScrollPosition={1} className="max-md:!hidden" />

							<InquiryBtn lastScrollPosition={100} />

							{process.env.NEXT_PUBLIC_SITE_TYPE === "toc" && (
								<CartDrawer>
									<div className="flex h-8 w-8 cursor-pointer items-center justify-center rounded-full text-brand-ink/45 transition-colors duration-200 hover:bg-brand-ink/5 hover:text-brand-ink max-md:!hidden">
										<i className="ri-shopping-cart-2-line relative text-[18px]">
											<CountBadge count={count} />
										</i>
									</div>
								</CartDrawer>
							)}

							<NavUser>
								<div className="flex h-8 w-8 items-center justify-center rounded-full text-brand-ink/45 transition-colors duration-200 hover:bg-brand-ink/5 hover:text-brand-ink">
									<i className="ri-user-line text-[18px]" />
								</div>
							</NavUser>

							<Translate lastScrollPosition={1} />
						</div>
					</div>
				</div>
			</header>

			{/* ---- Mobile menu ---- */}
			<div id="menu-mobile" className={openMenuMobile ? "open" : ""}>
				<div className="menu-container h-full bg-white">
					<div className="container h-full">
						<div className="menu-main h-full overflow-auto">
							<div className="heading relative flex items-center justify-center py-3">
								<div
									className="close-menu-mobile-btn absolute left-0 top-1/2 flex h-8 w-8 -translate-y-1/2 items-center justify-center rounded-full bg-brand-ink/5"
									onClick={handleMenuMobile}
								>
									<Icon.X size={16} />
								</div>
								<div
									onClick={() => GoToPage("/")}
									className="cursor-pointer text-center font-body text-[16px] font-semibold tracking-[0.04em] text-brand-ink"
								>
									LIGHTEW
								</div>
							</div>
							<div className="list-nav mt-6">
								<ul>
									{menus.map(
										(item) =>
											item.show && (
												<li className="mb-4 h-full" key={item.id}>
													{!item.hasSlug ? (
														<div
															className="border-brand-ink/8 flex items-center justify-between rounded-lg border px-4 py-3 font-body text-[13px] font-medium uppercase tracking-[0.04em] text-brand-ink"
															onClick={() => setMenuShow(item.id)}
														>
															<span>{t(item.name)}</span>
															<RightOutlined className="text-[10px] opacity-40" />
														</div>
													) : (
														<div
															onClick={() => GoToPage(item.link)}
															className="border-brand-ink/8 flex items-center justify-between rounded-lg border px-4 py-3 font-body text-[13px] font-medium uppercase tracking-[0.04em] text-brand-ink"
														>
															<span>{t(item.name)}</span>
															<RightOutlined className="text-[10px] opacity-40" />
														</div>
													)}

													{item.id === 2 && showService && (
														<div className="mt-2 space-y-1.5">
															{categories?.edges.map((catItem, index) => (
																<div key={catItem.node.id} className="rounded-lg pr-2 text-brand-ink">
																	<div className="border-brand-ink/6 flex items-center justify-between gap-2 rounded-md border px-2 py-1.5">
																		<div
																			className="flex items-center gap-2"
																			onClick={() => GoToPage(`/${catItem.node.slug}`)}
																		>
																			<Image
																				src={
																					filterCateImg(catItem.node.metadata)[0]?.url ||
																					"/image/default-image.webp"
																				}
																				width={40}
																				quality={50}
																				height={40}
																				alt={catItem.node.name}
																				className="h-[36px] w-[36px] rounded object-cover"
																			/>
																			<span className="font-body text-[13px] font-semibold">
																				{locale === defaultLocale
																					? catItem.node.name
																					: catItem.node?.translation?.name || catItem.node.name}
																			</span>
																		</div>
																		<button
																			className="text-brand-fg flex h-6 w-6 items-center justify-center rounded-full bg-brand/10"
																			onClick={() => setgs(index)}
																		>
																			{showGrandChildIndex === index ? (
																				<DownOutlined className="text-[9px]" />
																			) : (
																				<RightOutlined className="text-[9px]" />
																			)}
																		</button>
																	</div>

																	{showGrandChildIndex === index && catItem.node.children && (
																		<div className="ml-3 mt-1 space-y-0.5 pl-3">
																			{catItem.node.children.edges.map((child) => (
																				<div
																					key={child.node.slug}
																					className="cursor-pointer py-1.5"
																					onClick={() => GoToPage(`/${child.node.slug}`)}
																				>
																					<div className="flex items-center gap-2">
																						<Image
																							src={
																								filterCateImg(child.node.metadata)[0]?.url ||
																								"/image/default-image.webp"
																							}
																							width={32}
																							quality={50}
																							height={32}
																							alt={child.node.name}
																							className="rounded object-cover"
																						/>
																						<span className="font-body text-[13px] font-medium text-brand-ink/80">
																							{locale === defaultLocale
																								? child.node.name
																								: child.node.translation?.name || child.node.name}
																						</span>
																					</div>
																				</div>
																			))}
																		</div>
																	)}
																</div>
															))}
														</div>
													)}
													{item.id === showChildrenID && item.children && (
														<div className="mt-2 space-y-0.5 pl-4">
															{item.children.map((child, index) => (
																<div
																	key={index}
																	className="cursor-pointer py-2"
																	onClick={() => GoToPage(child.link)}
																>
																	<span className="hover:text-brand-fg font-body text-[13px] font-medium text-brand-ink/75">
																		{t(child.name)}
																	</span>
																</div>
															))}
														</div>
													)}
												</li>
											),
									)}
								</ul>
							</div>
						</div>
					</div>
				</div>
			</div>
		</>
	);
};

export default MenuTwo;
